import mixpanel from 'mixpanel-browser'

// Single shared Mixpanel instance for the PWA. Feature code must import the
// helpers below — never import `mixpanel-browser` directly elsewhere (see
// AGENTS.md). Telemetry must never block startup or offline use: when no token
// is configured every helper is a no-op.

let initialized = false

export function initMixpanel(): void {
  // Never send telemetry from local dev, even if a token leaks into the env.
  if (import.meta.env.DEV) return

  const token = import.meta.env.VITE_MIXPANEL_TOKEN
  if (!token) return

  mixpanel.init(token, {
    // EU data residency — matches the GDPR posture of the rest of Caliu.
    api_host: import.meta.env.VITE_MIXPANEL_API_HOST ?? 'https://api-eu.mixpanel.com',
    persistence: 'localStorage',
    // Auto-capture SPA route changes as page views.
    track_pageview: 'url-with-path',
  })
  // Attached to every event — lets us segment by client without per-call props.
  mixpanel.register({ platform: 'web' })
  initialized = true
  openProfile()
}

/**
 * Let profile writes reach Mixpanel before (or without) a sign-in.
 *
 * `mixpanel.people.set` is parked in a queue until `identify()` has run in this
 * page session, so on a device with no account every profile write (`is_pro`,
 * `locale`, `theme`) silently went nowhere and local-mode users read
 * `is_pro = undefined` in every report. Identifying the id the SDK already
 * holds — the `$device:` id, or the persisted user id of a restored session —
 * is the SDK-sanctioned way to open that queue: it registers no `$user_id`,
 * sends no `$identify`, and simply flushes profile updates to that id. The
 * later `identify(userId)` at sign-in still merges the device into the account.
 */
function openProfile(): void {
  mixpanel.identify(mixpanel.get_distinct_id())
}

/** Track a product event. No-op until `initMixpanel` has run with a token. */
export function track(event: string, properties?: Record<string, unknown>): void {
  if (!initialized) return
  mixpanel.track(event, properties)
}

/**
 * Identify the signed-in user and set their profile. Pass a stable internal id
 * (never an email). Profile attributes go to `people.set` — never as event
 * properties — so PII stays off the event stream.
 */
export function identify(userId: string, profile?: Record<string, unknown>): void {
  if (!initialized) return
  mixpanel.identify(userId)
  if (profile) mixpanel.people.set(profile)
}

/**
 * Update the current user's Mixpanel profile without re-identifying. Used to keep
 * segmentation attributes (is_pro, locale, theme, …) fresh as they change. No-op
 * until `initMixpanel` has run with a token. Keep PII (`$email`/`$name`) here too
 * — never on the event stream.
 */
export function setProfileProps(props: Record<string, unknown>): void {
  if (!initialized) return
  mixpanel.people.set(props)
}

/** Clear the distinct_id on logout so the next user starts a fresh identity. */
export function resetAnalytics(): void {
  if (!initialized) return
  mixpanel.reset()
  // `reset()` also wipes super properties, so re-register `platform` here or
  // every event fired after a logout ships without it.
  mixpanel.register({ platform: 'web' })
  // ...and closes the people queue again, so the fresh anonymous id needs the
  // same treatment as a first launch or its free-profile baseline is lost.
  openProfile()
}
